import { HTMLAttributes, useMemo, useRef, useState, MouseEvent } from 'react';
import { getCountryCallingCode } from 'react-phone-number-input';
import cn from 'classnames';
import { ReactComponent as InternationalIcon } from '@packages/shared/assets/international.svg';
import { ReactComponent as ArrowIcon } from '@packages/shared/assets/arrow.svg';
import { LocationCountry } from '@packages/shared/core/location/entities';
import { useUpdateEffect } from '@packages/shared/core/common/hooks';
import DropdownMenu, { DropdownMenuProps } from '@packages/shared/core/common/components/common/DropdownMenu';
import CountryFlag from '@packages/shared/core/common/components/common/CountryFlag';

import styles from './PhoneFieldSelect.module.scss';

type CountryCode = `${LocationCountry}`;

export type PhoneFieldSelectProps = {
  value: LocationCountry;
  options: Array<{ value: LocationCountry; label: string }>;
  onChange: (value: LocationCountry) => void;
  menuProps?: Partial<DropdownMenuProps>;
  toggleProps?: HTMLAttributes<HTMLDivElement>;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
};

const PhoneFieldSelect = (props: PhoneFieldSelectProps) => {
  const {
    value,
    options,
    onChange,
    menuProps = {},
    className,
    onFocus,
    onBlur,
    readOnly,
    disabled,
    toggleProps = {},
  } = props;

  const toggleRef = useRef<HTMLDivElement>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const getFormattedCountryCode = (code?: CountryCode) => {
    if (!code) return '';

    return `+${getCountryCallingCode(code)}`;
  };

  const countryOptions = useMemo(() => {
    return options.reduce(
      (
        acc: Array<{
          code: LocationCountry;
          callingCode: string;
          name: string;
        }>,
        option,
      ) => {
        if (!option || !option.value) return acc;

        acc.push({
          code: option.value,
          callingCode: getFormattedCountryCode(option.value),
          name: option.label,
        });

        return acc;
      },
      [],
    );
  }, [options]);

  const selectedOption = useMemo(() => {
    return countryOptions.find((option) => option.code === value);
  }, [value, countryOptions]);

  useUpdateEffect(() => {
    if (isDropdownVisible && onFocus) {
      onFocus();
      return;
    }

    if (onBlur) {
      onBlur();
    }
  }, [isDropdownVisible]);

  const onToggle = () => {
    if (readOnly || disabled) return;

    setIsDropdownVisible((v) => !v);
  };

  const onClose = () => {
    setIsDropdownVisible(false);
  };

  const handleSelect = (event: MouseEvent, countryCode: LocationCountry) => {
    event.stopPropagation();

    setIsDropdownVisible(false);
    onChange(countryCode);
  };

  const toggleIconClasses = cn({
    [styles.toggleIcon]: true,
    [styles.toggleIconActive]: isDropdownVisible,
  });

  return (
    <>
      <div
        {...toggleProps}
        className={cn(styles.toggle, toggleProps.className, className)}
        ref={toggleRef}
        role="button"
        onClick={onToggle}
      >
        {selectedOption ? (
          <CountryFlag className={styles.toggleFlag} code={selectedOption.code} />
        ) : (
          <InternationalIcon className={cn(styles.toggleFlag, styles.toggleInternationalIcon)} />
        )}

        <ArrowIcon className={toggleIconClasses} />
      </div>

      <DropdownMenu
        open={isDropdownVisible}
        anchorEl={toggleRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={onClose}
        classes={{
          list: cn(styles.countryMenu, menuProps.className),
          paper: cn(styles.countryMenuWrapper, menuProps.PaperProps?.className),
        }}
        className={styles.countryMenuRoot}
      >
        {countryOptions.map((option) => (
          <li key={option.code} className={styles.countyMenuItem} onClick={(event) => handleSelect(event, option.code)}>
            <div className={styles.country}>
              <CountryFlag className={styles.countryFlag} code={option.code} />

              <span className={styles.countryCallingCode}>{option.callingCode}</span>

              <span className={styles.countryName}>{option.name}</span>
            </div>
          </li>
        ))}
      </DropdownMenu>
    </>
  );
};

export default PhoneFieldSelect;
